import React, { useMemo, useState } from 'react';
import { Check, AlertTriangle, ArrowLeft } from 'lucide-react';
import type { OrchestratorProposal, OrchestratorPipelineCandidate } from '../../../types/smartStart';
import ClarifyingQuestionsForm from './ClarifyingQuestionsForm';

interface Props {
  proposal: OrchestratorProposal;
  isSubmitting: boolean;
  onBack: () => void;
  onConfirm: (pipelineKey: string, slotValues: Record<string, string>) => void;
}

const PipelineProposalV2: React.FC<Props> = ({ proposal, isSubmitting, onBack, onConfirm }) => {
  const candidates = useMemo<OrchestratorPipelineCandidate[]>(() => {
    const list: OrchestratorPipelineCandidate[] = [];
    const seen = new Set<string>();
    if (proposal.primary) {
      list.push(proposal.primary);
      seen.add(proposal.primary.pipeline_key);
    }
    for (const alt of proposal.alternatives || []) {
      if (!alt || seen.has(alt.pipeline_key)) continue;
      list.push(alt);
      seen.add(alt.pipeline_key);
    }
    return list;
  }, [proposal]);

  const [selectedKey, setSelectedKey] = useState<string | null>(
    proposal.primary?.pipeline_key || null,
  );
  const selected = useMemo(
    () => candidates.find((c) => c.pipeline_key === selectedKey) || null,
    [candidates, selectedKey],
  );

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
          Your goal
        </div>
        <p className="text-gray-900 mb-4">{proposal.user_goal}</p>

        {proposal.no_match || !proposal.primary ? (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-900">
                No strong pipeline match yet
              </div>
              <p className="text-sm text-amber-800 mt-1">
                Pick one of the options below or go back and describe your outcome differently.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-lavender/5 border border-lavender/30 rounded-xl">
            <div className="p-2 bg-lavender/10 rounded-lg flex-shrink-0">
              <Check className="w-4 h-4 text-lavender" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm font-semibold text-gray-900">Recommended pipeline</span>
                {proposal.primary.confidence != null && (
                  <span className="text-xs font-medium px-2 py-0.5 bg-lavender/10 text-lavender rounded-full">
                    {Math.round((proposal.primary.confidence || 0) * 100)}% confidence
                  </span>
                )}
              </div>
              {proposal.primary && (
                <p className="text-sm text-gray-700">{proposal.primary.reasoning}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {candidates.map((cand) => {
          const isSelected = selectedKey === cand.pipeline_key;
          return (
            <button
              key={cand.pipeline_key}
              onClick={() => setSelectedKey(cand.pipeline_key)}
              className={`text-left p-4 rounded-xl border-2 transition ${
                isSelected
                  ? 'border-lavender bg-lavender/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-bold text-gray-900">{cand.name}</div>
                {cand.confidence != null && (
                  <span className="text-xs font-medium text-gray-500">
                    {Math.round((cand.confidence || 0) * 100)}%
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 line-clamp-4">
                {cand.reasoning || cand.description || ''}
              </p>
            </button>
          );
        })}
      </div>

      {selected && (
        <ClarifyingQuestionsForm
          pipeline={selected}
          initialValues={proposal.intake_slots || {}}
          isSubmitting={isSubmitting}
          onBack={onBack}
          onSubmit={(values) => onConfirm(selected.pipeline_key, values)}
          submitLabel="Build my plan"
        />
      )}
    </div>
  );
};

export default PipelineProposalV2;
